import axiosClient from '../common/AxiosClient';

interface CertificateAttemptOptions {
  /**
   * The LearnUpon enrolment whose certificate is wanted — the card/page describes one attempt, so a
   * 2019 pass must serve ITS certificate, not the learner's newest for the course. Omit for a learning
   * path (its enrolment id is a different id space) or when no enrolment is known; the server then
   * serves the latest.
   */
  enrollmentId?: string | null;
  /** Portal hosting `enrollmentId` — LU enrolment ids collide across portals. */
  instance?: number | null;
  /** Certificate is still being malware-scanned (HTTP 202) — tell the caller to retry shortly. */
  onProcessing: () => void;
  onError: () => void;
}

interface OpenCertificatePdfOptions extends CertificateAttemptOptions {
  /** Course SKU the certificate was issued for. */
  sku: string;
}

interface OpenStudentCertificatePdfOptions extends CertificateAttemptOptions {
  /** Student whose certificate is wanted — the caller's own id for self-service, an arbitrary
   *  employee id for the training-manager path this hits. */
  employeeId: string;
  /** Course SKU the certificate was issued for. */
  sku: string;
}

/**
 * Fetches a certificate PDF from `url` and shows it in a new tab. Shared by every "View Certificate"
 * entry point (My Courses card, course-detail page, and the training-manager student/course
 * drill-downs), so the popup/202/blob handling cannot drift between them.
 */
const fetchAndOpenCertificatePdf = async (
  url: string,
  params: Record<string, string | number | undefined>,
  { onProcessing, onError }: Pick<CertificateAttemptOptions, 'onProcessing' | 'onError'>
): Promise<void> => {
  // Open the tab synchronously *before* the await — browsers block window.open when it
  // runs after a microtask boundary (treated as non-user-initiated). We navigate the tab
  // after the blob is ready. NOTE: do NOT pass 'noopener' here — it forces window.open to
  // return null, defeating the whole point of holding a handle to the placeholder tab.
  const pendingTab = typeof window !== 'undefined' ? window.open('about:blank', '_blank') : null;

  try {
    const response = await axiosClient.get(url, {
      responseType: 'blob',
      params,
    });

    // 202 Accepted is a success status, so it lands here (not the catch). It carries a JSON
    // "still being processed" body (malware scan pending) — never serve it as a PDF.
    if (response.status === 202) {
      pendingTab?.close();
      onProcessing();
      return;
    }

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);

    if (pendingTab) {
      pendingTab.location.href = blobUrl;
    } else {
      // Popup blocker swallowed the placeholder — try a direct open as a last resort.
      window.open(blobUrl, '_blank');
    }

    // Release the object URL after the new tab has had a chance to read it.
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30_000);
  } catch {
    pendingTab?.close();
    onError();
  }
};

/**
 * Downloads the active user's own certificate PDF for a course. The single implementation behind
 * every self-service "View Certificate" button (My Courses card, course-detail page).
 */
export const openCertificatePdf = async ({
  sku,
  enrollmentId,
  instance,
  onProcessing,
  onError,
}: OpenCertificatePdfOptions): Promise<void> =>
  fetchAndOpenCertificatePdf(
    `/Certificate/by-course/${encodeURIComponent(sku)}/download`,
    {
      // Guarded: axios omits undefined, but an EMPTY string would go on the wire and lean on
      // ASP.NET binding it back to null.
      learnUponEnrollmentId: enrollmentId || undefined,
      instance: instance || undefined,
    },
    { onProcessing, onError }
  );

/**
 * Downloads a specific student's certificate PDF for a course — the "View Certificate" row action in
 * the training-manager student/course drill-down dialogs. Manager-scoped counterpart of
 * {@link openCertificatePdf}; requires the caller to be an org admin or a training manager whose
 * scope covers `employeeId`.
 */
export const openStudentCertificatePdf = async ({
  employeeId,
  sku,
  enrollmentId,
  instance,
  onProcessing,
  onError,
}: OpenStudentCertificatePdfOptions): Promise<void> =>
  fetchAndOpenCertificatePdf(
    `/TrainingManager/students/${encodeURIComponent(employeeId)}/courses/${encodeURIComponent(sku)}/certificate/download`,
    {
      learnUponEnrollmentId: enrollmentId || undefined,
      instance: instance || undefined,
    },
    { onProcessing, onError }
  );
